import { uploadFileToS3 } from './uploadFileToS3';
import { resizeImageForUpload } from './resizeImageForUpload';

/**
 * Converts a data URL to a File object
 */
export const dataURLtoFile = (dataUrl: string, filename: string): File => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

/**
 * Uploads a cropped image (data URL) to S3
 */
export const uploadCroppedImage = async (
  dataUrl: string, 
  originalImageId: string,
  sopId?: string
): Promise<{ media_id: string; upload_url: string }> => {
  // Create a filename based on the original image ID and timestamp
  const timestamp = Date.now();
  const filename = `cropped_${originalImageId}_${timestamp}.jpg`;
  
  // Convert data URL to File and resize to standard resolution
  const file = dataURLtoFile(dataUrl, filename);
  const resized = await resizeImageForUpload(file);

  // Upload to S3
  const result = await uploadFileToS3(resized, sopId);
  
  if (!result || typeof result === 'string') {
    throw new Error('Failed to upload cropped image');
  }
  
  return result;
};

/**
 * Extracts cropped images from editor HTML content
 */
export const extractCroppedImages = (htmlContent: string): Array<{
  originalSrc: string;
  croppedData: string;
  imageId: string;
  width: number;
  height: number;
}> => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const images = doc.querySelectorAll('img[data-is-cropped="true"]');
  
  const croppedImages: Array<{
    originalSrc: string;
    croppedData: string;
    imageId: string;
    width: number;
    height: number;
  }> = [];
  
  images.forEach((img) => {
    const src = img.getAttribute('src');
    const croppedData = img.getAttribute('data-cropped-data');
    const imageId = img.getAttribute('data-image-id');
    const width = parseInt(img.getAttribute('width') || '300');
    const height = parseInt(img.getAttribute('height') || '200');
    
    if (src && croppedData && imageId) {
      croppedImages.push({
        originalSrc: src,
        croppedData,
        imageId: imageId.replace('image://', ''),
        width,
        height
      });
    }
  });
  
  return croppedImages;
};

/**
 * Replaces cropped image data URLs with new S3 URLs in HTML content
 */
export const replaceCroppedImageUrls = (
  htmlContent: string,
  replacements: Record<string, { url: string; mediaId: string }>
): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  
  // Find all images with cropped data
  const images = doc.querySelectorAll('img[data-is-cropped="true"]');
  
  images.forEach((img) => {
    const croppedData = img.getAttribute('data-cropped-data');
    if (croppedData && replacements[croppedData]) {
      const { url: newS3Url, mediaId } = replacements[croppedData];
      
      // Update src attribute
      img.setAttribute('src', newS3Url);
      
      // Update data-image-id attribute
      img.setAttribute('data-image-id', mediaId);
      
      // Remove cropped data attributes
      img.removeAttribute('data-is-cropped');
      img.removeAttribute('data-cropped-data');
    }
  });
  
  return doc.documentElement.outerHTML;
};

/**
 * Processes cropped images in HTML content by uploading them to S3 and updating references
 */
export const processCroppedImages = async (
  htmlContent: string,
  sopId: string,
  getImageUrls: (refIds: string[]) => Promise<Record<string, { url: string; expiresAt: number }>>
): Promise<string> => {
  const croppedImages = extractCroppedImages(htmlContent);
  
  if (croppedImages.length === 0) {
    return htmlContent;
  }
  
  try {
    const replacements: Record<string, { url: string; mediaId: string }> = {};
    
    // Upload each cropped image
    for (const croppedImage of croppedImages) {
      const uploadResult = await uploadCroppedImage(
        croppedImage.croppedData,
        croppedImage.imageId,
        sopId
      );
      
      // Get the download URL for the uploaded image
      const downloadUrls = await getImageUrls([uploadResult.media_id]);
      const urlState = downloadUrls[uploadResult.media_id];
      
      if (urlState) {
        replacements[croppedImage.croppedData] = {
          url: urlState.url,
          mediaId: uploadResult.media_id
        };
      }
    }
    
    // Replace cropped data URLs with S3 URLs
    return replaceCroppedImageUrls(htmlContent, replacements);
  } catch (error) {
    console.error('Error processing cropped images:', error);
    return htmlContent; // Return original content if processing fails
  }
};
